import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Megaphone, Plus, CheckCircle2, XCircle, Trash2, ExternalLink,
} from "lucide-react";
import { api, idr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import InputRupiah from "@/components/InputRupiah";
import { DatePicker } from "@/components/ui/date-picker";
import { formatTanggal, hariIniISO } from "@/lib/tanggal";
import { useCabangOptions } from "@/lib/cabang";
import {
  PageHeader, TidakPunyaAkses, SearchInput, StatusBadge, CAMPAIGN_STATUS_META,
  CHANNELS, CHANNEL_LABEL, SortTh, useSortState, sortRows, usePaginasi, Paginasi,
  unduhCsv, TombolEksporCsv, Modal, Field, inputCls, selectCls, textareaCls, InputCabang,
  usePinHapus, PinHapusDialog,
  FilterSelect, FilterRentangTanggal, TombolResetFilter, useOpsiFilter,
  adaFilterAktif, paramFilter,
} from "./_shared";

const emptyForm = () => ({
  nama: "", tujuan: "", brand: "", cabang: "", tanggal_mulai: hariIniISO(), tanggal_selesai: "",
  budget_plan: 0, catatan: "", line_items: [{ channel: CHANNELS[0], alokasi: 0, keterangan: "" }],
});

export default function Campaigns() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_campaign", "view");
  const bolehTambah = can("marketing_campaign", "create");
  const bolehApprove = can("marketing_campaign", "update");
  const bolehHapus = can("marketing_campaign", "delete");
  const brandOpsi = useCabangOptions({ denganKosong: true });

  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const [statusFilter, setStatusFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [cabangFilter, setCabangFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [pembuatFilter, setPembuatFilter] = useState("");
  const [periodeDari, setPeriodeDari] = useState("");
  const [periodeSampai, setPeriodeSampai] = useState("");
  const { sortKey, sortDir, toggle } = useSortState("created_at", "desc");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();
  const opsi = useOpsiFilter("/marketing/campaigns/opsi-filter", bolehLihat);

  // Pencarian & rentang periode DIKERJAKAN DI SERVER: daftar ini dipotong 2000
  // baris di backend, jadi menyaringnya di sini membuat campaign ke-2001 tidak
  // pernah ketemu dan layarnya melapor "tidak ada hasil" (CLAUDE.md).
  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "campaigns", statusFilter, brandFilter, cabangFilter, channelFilter,
      pembuatFilter, qDebounced, periodeDari, periodeSampai],
    () => api.get("/marketing/campaigns", {
      params: paramFilter({
        status: statusFilter, brand: brandFilter, cabang: cabangFilter,
        channel: channelFilter, dibuat_oleh: pembuatFilter, q: qDebounced.trim(),
        dari: periodeDari, sampai: periodeSampai,
      }),
    }).then((r) => r.data),
    { enabled: bolehLihat },
  );

  const rows = useMemo(() => data || [], [data]);
  const sorted = sortRows(rows, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(sorted.length, 15);
  const halaman = sorted.slice(awal, akhir);

  const filterAktif = adaFilterAktif(q, statusFilter, brandFilter, cabangFilter,
                                     channelFilter, pembuatFilter, periodeDari, periodeSampai);
  const resetFilter = () => {
    setQ(""); setStatusFilter(""); setBrandFilter(""); setCabangFilter("");
    setChannelFilter(""); setPembuatFilter(""); setPeriodeDari(""); setPeriodeSampai("");
  };

  const reload = () => refetch();

  const eksporCsv = () => {
    unduhCsv(`campaign-marketing-${hariIniISO()}.csv`, [
      { label: "Kode", value: (r) => r.code },
      { label: "Nama", value: (r) => r.nama },
      { label: "Brand", value: (r) => r.brand },
      { label: "Cabang", value: (r) => r.cabang || "" },
      { label: "Tanggal Mulai", value: (r) => r.tanggal_mulai },
      { label: "Tanggal Selesai", value: (r) => r.tanggal_selesai },
      { label: "Budget Plan", value: (r) => r.budget_plan },
      { label: "Total Alokasi", value: (r) => r.total_alokasi },
      { label: "Status", value: (r) => CAMPAIGN_STATUS_META[r.status]?.label || r.status },
      { label: "Dibuat Oleh", value: (r) => r.created_by_name || r.created_by || "" },
    ], sorted);
  };

  const simpan = async () => {
    if (!form.nama.trim()) { toast.error("Nama campaign wajib diisi"); return; }
    if (!form.tanggal_selesai) { toast.error("Tanggal selesai wajib diisi"); return; }
    if (!form.budget_plan || form.budget_plan <= 0) { toast.error("Budget plan wajib lebih dari 0"); return; }
    setBusy(true);
    try {
      const body = {
        ...form,
        line_items: form.line_items.filter((li) => li.channel).map((li) => ({ ...li, alokasi: Number(li.alokasi || 0) })),
      };
      const r = await api.post("/marketing/campaigns", body);
      toast.success(r.data.message || "Campaign diajukan");
      setForm(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan campaign");
    } finally { setBusy(false); }
  };

  const setujui = async (c) => {
    if (!window.confirm(`Setujui campaign "${c.nama}" (${idr(c.budget_plan)})?`)) return;
    try {
      const r = await api.post(`/marketing/campaigns/${c.id}/approve`);
      toast.success(r.data.message || "Campaign disetujui");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui campaign"); }
  };

  const tolak = async (c) => {
    const alasan = window.prompt(`Alasan menolak campaign "${c.nama}"?`) || "";
    if (!alasan.trim()) return;
    try {
      const r = await api.post(`/marketing/campaigns/${c.id}/reject`, { alasan });
      toast.success(r.data.message || "Campaign ditolak");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak campaign"); }
  };

  const hapus = async (c) => {
    if (!window.confirm(`Hapus campaign "${c.nama}"?`)) return;
    const aksi = (pin) => api.delete(`/marketing/campaigns/${c.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_campaign", targetId: c.id, ringkasan: `Hapus campaign ${c.nama}` });
    if (langsung) { toast.success("Campaign dihapus"); reload(); }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Megaphone} title="Campaign" />
        <TidakPunyaAkses label="Campaign Marketing" testid="mkt-camp-tanpa-akses" />
      </div>
    );
  }

  const totalAlokasiForm = (form?.line_items || []).reduce((s, li) => s + Number(li.alokasi || 0), 0);

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-campaigns">
      <PageHeader
        icon={Megaphone} title="Campaign"
        desc="Ajukan dan kelola campaign marketing beserta alokasi budget per channel."
        actions={<>
          <TombolEksporCsv onClick={eksporCsv} testid="mkt-camp-export" />
          {bolehTambah && (
            <button onClick={() => setForm(emptyForm())} data-testid="mkt-camp-tambah"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Ajukan Campaign
            </button>
          )}
        </>}
      />

      {/* Toolbar filter — seluruhnya dikirim ke server, lihat komentar di atas */}
      <div className="flex items-center gap-2 flex-wrap">
        <SearchInput value={q} onChange={setQ} placeholder="Cari nama/kode/tujuan campaign..." testid="mkt-camp-cari" className="w-64" />
        <FilterSelect value={statusFilter} onChange={setStatusFilter} testid="mkt-camp-filter-status"
                      semuaLabel="Semua Status"
                      opsi={Object.entries(CAMPAIGN_STATUS_META).map(([k, v]) => ({ value: k, label: v.label }))} />
        {/* Opsi brand/cabang diambil dari SELURUH koleksi campaign (endpoint
            opsi-filter), bukan dari daftar cabang master: yang berguna disaring
            adalah nilai yang benar-benar dipakai campaign. */}
        <FilterSelect value={brandFilter} onChange={setBrandFilter} testid="mkt-camp-filter-brand"
                      semuaLabel="Semua Brand" opsi={opsi.brand} />
        <FilterSelect value={cabangFilter} onChange={setCabangFilter} testid="mkt-camp-filter-cabang"
                      semuaLabel="Semua Cabang" opsi={opsi.cabang} />
        {/* "Platform" versi Campaign = channel alokasi budget-nya. */}
        <FilterSelect value={channelFilter} onChange={setChannelFilter} testid="mkt-camp-filter-channel"
                      semuaLabel="Semua Channel"
                      opsi={CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABEL[c] }))} />
        <FilterSelect value={pembuatFilter} onChange={setPembuatFilter} testid="mkt-camp-filter-pembuat"
                      semuaLabel="Semua User" opsi={opsi.dibuat_oleh} />
        <FilterRentangTanggal dari={periodeDari} sampai={periodeSampai}
                              onDari={setPeriodeDari} onSampai={setPeriodeSampai}
                              testid="mkt-camp-periode" labelDari="Periode dari" labelSampai="Periode s/d" />
        <TombolResetFilter aktif={filterAktif} onReset={resetFilter} testid="mkt-camp-reset-filter" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <SortTh label="Kode / Nama" sortKeyName="nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-camp-sort-nama" />
                <SortTh label="Brand / Cabang" sortKeyName="brand" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-camp-sort-brand" />
                <SortTh label="Periode" sortKeyName="tanggal_mulai" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-camp-sort-periode" />
                <SortTh label="Budget" sortKeyName="budget_plan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-camp-sort-budget" />
                <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-camp-sort-status" />
                <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && halaman.length === 0}
                            varian="tabel" kolom={6} pesanKosong="Belum ada campaign yang cocok." data-testid="mkt-camp-state">
                {halaman.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/60" data-testid={`mkt-camp-row-${c.id}`}>
                    <td className="px-3 py-2.5">
                      <p className="font-mono text-[11px] text-slate-500">{c.code}</p>
                      <Link to={`/marketing/campaigns/${c.id}`} className="text-slate-900 font-medium hover:text-emerald-700 hover:underline">
                        {c.nama}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{c.brand || "-"}{c.cabang ? ` / ${c.cabang}` : ""}</td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{formatTanggal(c.tanggal_mulai)} – {formatTanggal(c.tanggal_selesai)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{idr(c.budget_plan)}</td>
                    <td className="px-3 py-2.5"><StatusBadge meta={CAMPAIGN_STATUS_META} value={c.status} /></td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {c.status === "pending_approval" && bolehApprove && (
                          <>
                            <button onClick={() => setujui(c)} data-testid={`mkt-camp-setujui-${c.id}`}
                                    className="px-2 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Setujui
                            </button>
                            <button onClick={() => tolak(c)} data-testid={`mkt-camp-tolak-${c.id}`}
                                    className="px-2 h-8 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium inline-flex items-center gap-1">
                              <XCircle className="w-3.5 h-3.5" /> Tolak
                            </button>
                          </>
                        )}
                        {["pending_approval", "rejected"].includes(c.status) && bolehHapus && (
                          <button onClick={() => hapus(c)} data-testid={`mkt-camp-hapus-${c.id}`}
                                  className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <Link to={`/marketing/campaigns/${c.id}`} data-testid={`mkt-camp-detail-${c.id}`}
                              className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </div>
        <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-camp-paginasi" />
      </div>

      {/* Form tambah campaign */}
      <Modal open={!!form} onClose={() => setForm(null)} title="Ajukan Campaign Baru" maxWidth="max-w-2xl" testid="mkt-camp-modal"
             footer={<>
               <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpan} disabled={busy} data-testid="mkt-camp-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                 {busy ? "Menyimpan..." : "Ajukan"}
               </button>
             </>}>
        {form && (
          <>
            <Field label="Nama Campaign" required>
              <input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))}
                     data-testid="mkt-camp-f-nama" className={inputCls} placeholder="cth. Ramadan Sale 2026" />
            </Field>
            <Field label="Tujuan">
              <input value={form.tujuan} onChange={(e) => setForm((f) => ({ ...f, tujuan: e.target.value }))}
                     data-testid="mkt-camp-f-tujuan" className={inputCls} placeholder="cth. Meningkatkan penjualan unit second" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand">
                <InputCabang value={form.brand} onChange={(v) => setForm((f) => ({ ...f, brand: v }))}
                             opsi={brandOpsi} testid="mkt-camp-f-brand" />
              </Field>
              <Field label="Cabang (opsional)">
                <InputCabang value={form.cabang} onChange={(v) => setForm((f) => ({ ...f, cabang: v }))}
                             opsi={brandOpsi} testid="mkt-camp-f-cabang" placeholder="Kosongkan jika lintas cabang" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tanggal Mulai" required>
                <DatePicker value={form.tanggal_mulai} onChange={(v) => setForm((f) => ({ ...f, tanggal_mulai: v }))} data-testid="mkt-camp-f-mulai" />
              </Field>
              <Field label="Tanggal Selesai" required>
                <DatePicker value={form.tanggal_selesai} onChange={(v) => setForm((f) => ({ ...f, tanggal_selesai: v }))}
                            min={form.tanggal_mulai} data-testid="mkt-camp-f-selesai" />
              </Field>
            </div>
            <Field label="Budget Plan (Rp)" required>
              <InputRupiah value={form.budget_plan} onChange={(v) => setForm((f) => ({ ...f, budget_plan: v }))}
                           testid="mkt-camp-f-budget" className={inputCls} />
            </Field>

            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-700">Alokasi per Channel</p>
                <p className={`text-xs font-mono ${totalAlokasiForm > (form.budget_plan || 0) ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                  {idr(totalAlokasiForm)} / {idr(form.budget_plan || 0)}
                </p>
              </div>
              <div className="space-y-2">
                {form.line_items.map((li, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select value={li.channel} data-testid={`mkt-camp-f-li-channel-${idx}`}
                            onChange={(e) => setForm((f) => ({ ...f, line_items: f.line_items.map((x, i) => i === idx ? { ...x, channel: e.target.value } : x) }))}
                            className={`${selectCls} w-44 h-9`}>
                      {CHANNELS.map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
                    </select>
                    <InputRupiah value={li.alokasi} testid={`mkt-camp-f-li-alokasi-${idx}`}
                                 onChange={(v) => setForm((f) => ({ ...f, line_items: f.line_items.map((x, i) => i === idx ? { ...x, alokasi: v } : x) }))}
                                 className={`${inputCls} h-9 w-32`} />
                    <input value={li.keterangan} placeholder="Keterangan" data-testid={`mkt-camp-f-li-ket-${idx}`}
                           onChange={(e) => setForm((f) => ({ ...f, line_items: f.line_items.map((x, i) => i === idx ? { ...x, keterangan: e.target.value } : x) }))}
                           className={`${inputCls} h-9 flex-1`} />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }))}
                            data-testid={`mkt-camp-f-li-hapus-${idx}`} className="w-9 h-9 rounded hover:bg-rose-50 text-rose-500 grid place-items-center shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setForm((f) => ({ ...f, line_items: [...f.line_items, { channel: CHANNELS[0], alokasi: 0, keterangan: "" }] }))}
                      data-testid="mkt-camp-f-li-tambah" className="mt-2 text-xs font-medium text-emerald-700 hover:underline inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Tambah baris alokasi
              </button>
            </div>

            <Field label="Catatan">
              <textarea value={form.catatan} rows={2} data-testid="mkt-camp-f-catatan"
                        onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>

      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(reload)} />
    </div>
  );
}
